import { makeAutoObservable } from 'mobx';
import {
  getLocalStorageValue,
  setLocalStorageValue,
} from '../utils/localStorage';
import {
  LAST_SHOWED_BANNER_ID,
  TRANSPORTER_BANNER_KEY,
  SAFETY_BANNER_KEY,
  ETRN_BANNER_KEY,
  AVG_PRICE_BANNER_KEY,
} from '../constants/localStorageKeys';
import { hasPriceStatisticsLicense } from '../constants/payedServices';

class BannerStore {
  static BANNER_TYPES = {
    TRANSPORTER: 'transporter',
    SAFETY: 'safety',
    ETRN: 'etrn',
    AVG_PRICE: 'avgPrice',
  };

  // Конфигурация баннеров
  banners = {
    [BannerStore.BANNER_TYPES.TRANSPORTER]: {
      enablerId: TRANSPORTER_BANNER_ENABLER_ID, // eslint-disable-line
      localStorageKey: TRANSPORTER_BANNER_KEY,
      imageUrl: '/rating/assets/images/truck-lightning.svg',
    },
    [BannerStore.BANNER_TYPES.SAFETY]: {
      enablerId: SAFETY_BANNER_ENABLER_ID, // eslint-disable-line
      localStorageKey: SAFETY_BANNER_KEY,
      imageUrl: '/rating/assets/images/safety-banner-icon.svg',
    },
    [BannerStore.BANNER_TYPES.ETRN]: {
      enablerId: ETRN_BANNER_ENABLER_ID, // eslint-disable-line
      localStorageKey: ETRN_BANNER_KEY,
      imageUrl: '/rating/assets/images/epd-medal-banner.svg',
    },
    [BannerStore.BANNER_TYPES.AVG_PRICE]: {
      enablerId: AVG_PRICE_ENABLER_ID, // eslint-disable-line
      localStorageKey: AVG_PRICE_BANNER_KEY,
      imageUrl: '/rating/assets/images/avg-prices.svg',
    },
  };

  currentBannerToShow = null;
  featureStore = null;

  constructor(featureStore) {
    this.featureStore = featureStore;
    makeAutoObservable(this);
  }

  // Инициализация баннеров (вызывается после загрузки featureStore)
  initializeBanners = () => {
    this.selectBannerToShow();
  };

  // Получает список всех активных типов баннеров
  getBannerTypes = () => {
    const allTypes = Object.values(BannerStore.BANNER_TYPES);
    return allTypes.filter((type) => {
      // Проверяем глобальные фичи
      if (type === BannerStore.BANNER_TYPES.TRANSPORTER && !this.featureStore.isActiveTransporterBannerSiteFeatureActive) {
        return false;
      }
      if (type === BannerStore.BANNER_TYPES.SAFETY && !this.featureStore.isSafetyBannerSiteFeatureActive) {
        return false;
      }
      if (type === BannerStore.BANNER_TYPES.ETRN && !this.featureStore.isEtrnBannerSiteFeatureActive) {
        return false;
      }
      // avg-price показываем только при активной фиче и при отсутствии лицензии «Статистика цен»
      if (type === BannerStore.BANNER_TYPES.AVG_PRICE
        && (!this.featureStore.isAvgPriceBannerSiteFeatureActive || hasPriceStatisticsLicense())) {
        return false;
      }

      return true;
    });
  };

  // Получает список доступных баннеров
  getAvailableBanners = () => this.getBannerTypes().filter((type) => {
    const bannerConfig = this.banners[type];
    return this.featureStore.isBannerAvailable(bannerConfig.enablerId);
  });

  // Находит тип баннера по enablerId
  findBannerTypeByEnablerId = enablerId => Object.entries(this.banners)
    .find(([, config]) => config.enablerId === Number(enablerId))?.[0];

  // Получает следующий баннер в чередовании
  getNextBannerInRotation = (currentBannerType) => {
    const availableBanners = this.getAvailableBanners();

    if (availableBanners.length === 0) return null;
    if (availableBanners.length === 1) return availableBanners[0];

    const currentIndex = availableBanners.indexOf(currentBannerType);

    // Если текущий баннер не найден среди доступных (был закрыт), начинаем с первого
    if (currentIndex === -1) {
      return availableBanners[0];
    }

    const nextIndex = (currentIndex + 1) % availableBanners.length;
    return availableBanners[nextIndex];
  };

  // Выбирает баннер для показа на основе чередования и доступности
  selectBannerToShow = () => {
    const availableBanners = this.getAvailableBanners();

    if (availableBanners.length === 0) {
      this.currentBannerToShow = null;
      return;
    }

    if (availableBanners.length === 1) {
      const [bannerType] = availableBanners;
      this.currentBannerToShow = bannerType;
      this.saveLastShownBanner(this.currentBannerToShow);
      return;
    }

    // Если доступно несколько баннеров - используем чередование
    const lastShownBannerId = getLocalStorageValue(LAST_SHOWED_BANNER_ID);

    if (!lastShownBannerId) {
      // Выбираем первый доступный баннер
      [this.currentBannerToShow] = availableBanners;
    } else {
      const lastBannerType = this.findBannerTypeByEnablerId(lastShownBannerId);
      const nextBanner = this.getNextBannerInRotation(lastBannerType);

      // Используем следующий баннер или первый доступный если что-то пошло не так
      this.currentBannerToShow = nextBanner || availableBanners[0];
    }

    this.saveLastShownBanner(this.currentBannerToShow);
  };

  // Сохраняет последний показанный баннер
  saveLastShownBanner = (bannerType) => {
    const enablerId = this.banners[bannerType]?.enablerId;
    if (enablerId) {
      setLocalStorageValue(LAST_SHOWED_BANNER_ID, enablerId);
    }
  };

  // Определяет должен ли показываться баннер
  shouldShowBanner = (bannerType) => {
    const bannerConfig = this.banners[bannerType];
    if (!bannerConfig) return false;

    return this.currentBannerToShow === bannerType && this.featureStore.isBannerAvailable(bannerConfig.enablerId);
  };

  // Закрывает баннер
  closeBanner = async (bannerType) => {
    const config = this.banners[bannerType];
    if (!config) return;

    await this.featureStore.setBannerViewed(config.enablerId, config.localStorageKey);
  };

  // Методы для HOC-компонентов
  putTransporterBannerViewedFlag = () => this.closeBanner(BannerStore.BANNER_TYPES.TRANSPORTER);
  putSafetyBannerViewedFlag = () => this.closeBanner(BannerStore.BANNER_TYPES.SAFETY);
  putReadyForEtrnBannerViewedFlag = () => this.closeBanner(BannerStore.BANNER_TYPES.ETRN);
  putAvgPriceBannerViewedFlag = () => this.closeBanner(BannerStore.BANNER_TYPES.AVG_PRICE);
}

export default BannerStore;
